import {
  Modal,
  Grid,
  Button,
  Avatar,
  Separator,
  List,
  Input,
  DatePicker,
} from '@design-systems/canvas-ui'
import { useState, useEffect } from 'react'
import { getFiltersForVettingSummary } from '../../services/lcmService'
import { transformFilterResponse } from '../../common/rfiHelper'
import { RFI_LAST_MODIFIED_DATE } from '../../common/commonConstants'

const TableFilterModal = (props) => {
  const [activeFilterOption, setActiveFilterOption] = useState('')
  const [keyword, setKeyword] = useState('')
  const [filterData, setFilterData] = useState({})
  const [allSelectedFilters, setAllSelectedFilters] = useState({})

  const isDateRangeFilter = (filterKey) => {
    return filterKey === RFI_LAST_MODIFIED_DATE
  }

  const getfilters = async (vettingType) => {
    const filterResponse = await getFiltersForVettingSummary(vettingType)
    const transformedResponse = transformFilterResponse(filterResponse)

    setFilterData(transformedResponse)
    setActiveFilterOption(Object.keys(transformedResponse)[0] || '')

    if (props.handleFilterDataUpdate) {
      props.handleFilterDataUpdate(transformedResponse)
    }
  }

  useEffect(() => {
    if (props.vettingType) {
      getfilters(props.vettingType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.vettingType])

  useEffect(() => {
    setKeyword('')
    setAllSelectedFilters({})
    setActiveFilterOption('')
  }, [props.vettingType])

  useEffect(() => {
    if (props.allSelectedFilters) {
      setAllSelectedFilters(props.allSelectedFilters)
    }
  }, [props.allSelectedFilters])

  const getFilterName = (key) => {
    return filterData[key]?.name || key
  }

  const getSelectedCountForFilter = (filterKey) => {
    const selectedValue = allSelectedFilters[filterKey]

    if (isDateRangeFilter(filterKey)) {
      return selectedValue?.startDate || selectedValue?.endDate ? 1 : 0
    }

    return selectedValue?.length || 0
  }

  const getTotalSelectedCount = () => {
    return Object.keys(allSelectedFilters).reduce((total, filterKey) => {
      return total + getSelectedCountForFilter(filterKey)
    }, 0)
  }

  const renderFilterOptions = () => {
    return Object.keys(filterData).map((filterKey, index) => {
      const selectedCount = getSelectedCountForFilter(filterKey)

      return (
        <List.Item
          key={filterKey}
          className="hc8-pv-none"
          isSelected={filterKey === activeFilterOption}
          data-testid={`filter-option-${index}`}
          onClick={() => {
            setActiveFilterOption(filterKey)
            setKeyword('')
          }}
        >
          <p className="vetting-filter-option hc8-clr-base hc8-pv-3x hc8-fs-150 hc8-pl-3x hc8-ma-none">
            {getFilterName(filterKey)}

            {selectedCount > 0 && (
              <Avatar
                aria-label="filter count"
                size="sm"
                color="blue"
                className="vetting-filter-count hc8-mr-2x"
              >
                {selectedCount}
              </Avatar>
            )}
          </p>
        </List.Item>
      )
    })
  }

  const handleCheckboxToggle = (event, selectedItem) => {
    const isChecked = event.currentTarget.checked

    setAllSelectedFilters((previousFilters) => {
      const selectedValues = previousFilters[activeFilterOption] || []

      const updatedValues = isChecked
        ? [...selectedValues, selectedItem.id]
        : selectedValues.filter((id) => id !== selectedItem.id)

      return {
        ...previousFilters,
        [activeFilterOption]: updatedValues,
      }
    })
  }

  const renderDateRangeFilter = () => {
    const selectedDateRange = allSelectedFilters[RFI_LAST_MODIFIED_DATE] || null

    return (
      <div className="hc8-pa-3x vetting-date-filter">
        <DatePicker
          id="rfi-last-modified-date"
          label="RFI Last Modified Date"
          variant="range"
          disableDates={{ future: true }}
          value={selectedDateRange}
          quickSelectRange
          location="bottom-left"
          onChange={(id, value) => {
            setAllSelectedFilters((previousFilters) => ({
              ...previousFilters,
              [RFI_LAST_MODIFIED_DATE]: value || null,
            }))
          }}
          errorText="Please enter a valid date range"
        />
      </div>
    )
  }

  const renderMultiSelectFilter = () => {
    const filterValues = filterData[activeFilterOption]?.values || []

    const filteredValues = keyword
      ? filterValues.filter((item) =>
          item.value.toLowerCase().includes(keyword.toLowerCase()),
        )
      : filterValues

    return (
      <div className="vetting-filter-values">
        <div className="hc8-ov-auto vetting-filter-checkbox-list">
          {filteredValues.map((item, index) => {
            const isSelected = allSelectedFilters[activeFilterOption]?.some(
              (selectedId) => selectedId === item.id,
            )

            return (
              <div
                className="vetting-filter-checkbox-item"
                key={`checkbox-wrap-${item.id}-${index}`}
              >
                <Input.Checkbox
                  id={`checkbox-${item.id}-${index}`}
                  label={item.value}
                  className="hc8-clr-disabled vetting-filter-checkbox"
                  isChecked={isSelected || false}
                  onChange={(event) => handleCheckboxToggle(event, item)}
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderFilterValues = () => {
    if (!activeFilterOption) {
      return null
    }

    if (isDateRangeFilter(activeFilterOption)) {
      return renderDateRangeFilter()
    }

    return renderMultiSelectFilter()
  }

  const clearAllFilters = () => {
    setAllSelectedFilters({})
  }

  const applyFilters = () => {
    if (props.handleFilterUpdate) {
      props.handleFilterUpdate(allSelectedFilters)
    }

    if (props.closeModal) {
      props.closeModal()
    }
  }

  const modalFooter = (
    <div className="vetting-filter-footer">
      <Button
        data-testid="button-clear-all"
        variant="secondary"
        isDisabled={getTotalSelectedCount() === 0}
        onClick={clearAllFilters}
      >
        <span className="hc8-mr-2x">Clear All</span>

        <Avatar aria-label="total filter" size="sm" color="blue">
          {getTotalSelectedCount()}
        </Avatar>
      </Button>

      <Button variant="primary" onClick={applyFilters}>
        Apply
      </Button>
    </div>
  )

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.closeModal}
      headingText="Apply Filters"
      className="filter-modal vetting-filter-modal lcm-vetting-filter-modal"
      size="md"
      spacing="normal"
      scrollBehavior="contained"
      footer={modalFooter}
    >
      <Grid.Container
        className="filter-modal-body vetting-filter-body"
        spacing="none"
      >
        <Grid.Item
          size={{ xs: 4 }}
          className="hc8-pa-none filter-left-grid vetting-filter-nav"
        >
          <List className="hc8-pr-none hc8-wth-100 vetting-filter-list">
            {renderFilterOptions()}
          </List>
        </Grid.Item>

        <Separator direction="vertical" />

        <Grid.Item
          className="filter-options-wrapper vetting-filter-content"
          size={{ xs: 8 }}
        >
          {renderFilterValues()}
        </Grid.Item>
      </Grid.Container>
    </Modal>
  )
}

export default TableFilterModal
